import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import { createAsyncThunk } from '@reduxjs/toolkit';
import { initialState } from "../initial";
import { ErrorCodes, listen, login, PxError, removeListener } from '@chegg/one-auth-sdk';
import { LoginErrorsEnum } from "../../lib/constants";
import { PxCaptchaTypes, View } from "../types";
import { setGenericError, setMfa } from "../sharedActions";
import { notificationsRioEvent, signinSuccessRioEvent } from "../../lib/rio/rioEvents";
import { isValidEmail } from "../../utils/passwordValidations";
import { EventTypes, recordEvents } from "../../utils/newRelicEvents";
import { initiatePx } from "../../utils/pxUtils";
import { getCurrentView } from "../helpers";
import { getTexts } from "../../lib/contentful/defaultTexts";
export { createLoginThunk };
export var sdkLogin = createLoginThunk('sdkLogin', /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(state) {
    var clientId, _state$signin$credent, email, password;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          clientId = state.clientId, _state$signin$credent = state.signin.credentials, email = _state$signin$credent.email.value, password = _state$signin$credent.password.value;
          recordEvents(_defineProperty({}, EventTypes.loginSent, {
            clientId: clientId,
            authType: 'email/password'
          }));
          _context.next = 4;
          return login({
            email: email,
            password: password
          });
        case 4:
        case "end":
          return _context.stop();
      }
    }, _callee);
  }));
  return function (_x) {
    return _ref.apply(this, arguments);
  };
}());
export var sdkLoginReducers = {
  fulfilled: function fulfilled(state) {
    state.signin.loading = false;
    state.signin.credentials.error = '';
    state.sso.error = '';
  },
  pending: function pending(state) {
    state.signin.loading = true;
    if (state.mfa.showMfa && state.mfa.codeValidationError) {
      state.mfa.codeValidationError = '';
    }
    state.sso.error = '';
  },
  rejected: function rejected(state, action) {
    var _action$type;
    var error = action.payload;
    var view = getCurrentView(state);
    if (!view) return;
    var isSso = ((_action$type = action.type) === null || _action$type === void 0 ? void 0 : _action$type.indexOf("Sso")) > -1;
    var clientId = state.clientId;
    var ssoProvider = state.sso.authProvider;
    var authType = isSso ? ssoProvider : 'email/password';
    var email = isSso ? state.sso.email : state.signin.credentials.email.value;
    state.signin.loading = error instanceof PxError;
    sendNotificationsRioEvent(error.code, state, view, isSso);
    sendNewRelicErrorEvent(error, isSso, clientId, authType);
    switch (error.code) {
      case ErrorCodes.user_not_found:
        if (isSso) {
          if (view === View.SIGNIN) {
            state.signin.credentials = initialState.signin.credentials;
            state.sso.error = LoginErrorsEnum.NO_CHEGG_ACCOUNT;
            return;
          }
          if (view === View.SIGNUP) {
            state.signup.isSso = true;
            state.signup.showUserInfo = true;
            state.signup.credentials.password = initialState.signup.credentials.password;
            state.signin.credentials = initialState.signup.credentials;
            state.sso.error = '';
            return;
          }
        }
        state.signin.credentials.error = getTexts().login.noMatchError;
        state.sso.error = '';
        return;
      case ErrorCodes.invalid_email:
        state.signin.credentials.error = getTexts().login.emailError;
        state.sso.error = '';
        return;
      case ErrorCodes.wrong_password:
        state.signin.credentials.error = getTexts().login.noMatchError;
        state.sso.error = '';
        return;
      case ErrorCodes.merged_client_credentials_used:
        state.signin.credentials.error = getTexts().login.mergedClientCredentialsUsedError;
        state.sso.error = '';
        return;
      case ErrorCodes.inactive_user:
        state[view].credentials.error = LoginErrorsEnum.LOCKED_SUSPICIOUS_ACTIVITY;
        if (state.sso.email && state.sso.authProviderToken && state.sso.authProvider) {
          state.resetPassword.credentials.email.value = state.sso.email;
          state.resetPassword.credentials.email.valid = isValidEmail(state.sso.email);
        }
        recordEvents(_defineProperty({}, EventTypes.mfaInactiveUser, {
          error: error.code,
          clientId: clientId,
          authType: authType,
          email: email
        }));
        return;
    }
    if (!(error instanceof PxError)) {
      state.signin.credentials.error = getTexts().login.genericError;
    }
    if (state.mfa.showMfa) {
      state.mfa.codeValidationError = 'unknown_mfa_error';
      recordEvents(_defineProperty({}, EventTypes.invalidMfaCode, {
        error: error.code,
        clientId: clientId,
        authType: authType,
        email: email
      }));
    }
  }
};

/**
 * This function facilitates sharing the thunk logic between login and ssoLogin.
 *
 * The passed action handles extracting info from the store and calling the SDK with that info.
 *
 * @param name
 * @param action
 */
function createLoginThunk(name, action) {
  return createAsyncThunk('oneauth/' + name, /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(params, context) {
      var getState, dispatch, rejectWithValue, state, isSso, ssoProvider, error, authType, env, clientId, onLoginSuccessEvent;
      return _regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) switch (_context2.prev = _context2.next) {
          case 0:
            onLoginSuccessEvent = function _onLoginSuccessEvent() {
              signinSuccessRioEvent(state.clientId === 'CJP' && 'internships' || ( // @ts-ignore
              params === null || params === void 0 ? void 0 : params.provider) || 'chegg');
              var eventType = isSso && EventTypes.ssoLoginSuccess || EventTypes.loginSuccess;
              recordEvents(_defineProperty({}, eventType, {
                clientId: clientId,
                authType: authType
              }));
            };
            getState = context.getState, dispatch = context.dispatch, rejectWithValue = context.rejectWithValue;
            state = getState();
            isSso = name === 'sdkSsoLogin';
            ssoProvider = state.sso.authProvider;
            error = state.signin.credentials.error;
            authType = isSso ? ssoProvider : 'email/password';
            env = state.env;
            clientId = state.clientId;
            if (error) dispatch(setGenericError({
              error: ''
            }));
            _context2.prev = 10;
            listen('login.success', onLoginSuccessEvent);
            _context2.next = 14;
            return action(state, params);
          case 14:
            _context2.next = 28;
            break;
          case 16:
            _context2.prev = 16;
            _context2.t0 = _context2["catch"](10);
            removeListener('login.success', onLoginSuccessEvent);
            _context2.t1 = _context2.t0.code;
            _context2.next = _context2.t1 === ErrorCodes.mfa_required ? 22 : 26;
            break;
          case 22:
            dispatch(setMfa({
              isSso: isSso,
              showMfa: true,
              codeValidationError: '',
              mfaPayload: _context2.t0.mfaPayload,
              authProvider: ssoProvider || ''
            }));
            recordEvents(_defineProperty({}, EventTypes.mfaRequired, {
              clientId: clientId,
              authType: authType,
              error: _context2.t0.message
            }));
            listen('login.success', onLoginSuccessEvent);
            return _context2.abrupt("return");
          case 26:
            if (_context2.t0 instanceof PxError) {
              initiatePx(env, _context2.t0, dispatch, clientId, PxCaptchaTypes.LOGIN, authType, false, function () {
                if (!isSso) {
                  dispatch(sdkLogin());
                }
              });
            }
            return _context2.abrupt("return", rejectWithValue(_context2.t0));
          case 28:
          case "end":
            return _context2.stop();
        }
      }, _callee2, null, [[10, 16]]);
    }));
    return function (_x2, _x3) {
      return _ref2.apply(this, arguments);
    };
  }());
}
function sendNotificationsRioEvent(errorCode, state, view) {
  var isSso = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;
  var message_detail = getTexts().login.genericError;
  var message_id = 'sign in sdk: genericError';
  switch (errorCode) {
    case ErrorCodes.user_not_found:
      {
        if (isSso) {
          if (view === View.SIGNIN) {
            message_detail = "".concat(getTexts().login.noCheggAccountError, " ").concat(getTexts().login.createAccountLabel);
            message_id = "sign in sso sdk: ".concat(state.sso.authProvider || '', " ").concat(ErrorCodes.user_not_found);
          } else {
            return;
          }
        } else {
          message_detail = getTexts().login.noMatchError;
          message_id = "sign in sdk: ".concat(ErrorCodes.user_not_found);
        }
        break;
      }
    case ErrorCodes.invalid_email:
      {
        message_detail = getTexts().login.emailError;
        message_id = "sign in sdk: ".concat(ErrorCodes.invalid_email);
        break;
      }
    case ErrorCodes.wrong_password:
      {
        message_detail = getTexts().login.noMatchError;
        message_id = "sign in sdk: ".concat(ErrorCodes.wrong_password);
        break;
      }
    case ErrorCodes.inactive_user:
      {
        message_detail = "".concat(getTexts().login.passwordForceResetError).concat(getTexts().login.passwordForceResetLinkError).concat(getTexts().login.passwordForceResetTwoError).concat(getTexts().login.passwordForceResetLearnMoreLinkError);
        message_id = "sign in sdk: ".concat(ErrorCodes.inactive_user);
        break;
      }
    case ErrorCodes.merged_client_credentials_used:
      {
        message_detail = "".concat(getTexts().login.mergedClientCredentialsUsedError);
        message_id = "sign in sdk: ".concat(ErrorCodes.merged_client_credentials_used);
        break;
      }
  }
  notificationsRioEvent({
    notifications: {
      message_id: message_id,
      type: 'error',
      message_detail: message_detail,
      message_name: 'sign in error'
    }
  });
}
function sendNewRelicErrorEvent(error, isSso, clientId, authType) {
  var errorEventType = isSso ? EventTypes.ssoLoginFail : EventTypes.loginFail;
  var mfaError = error.code === ErrorCodes.inactive_user || error.code === ErrorCodes.mfa_required;
  if (mfaError) return;
  if (error.code in ErrorCodes) {
    recordEvents(_defineProperty({}, errorEventType, {
      clientId: clientId,
      authType: authType,
      error: error.message,
      isCriticalError: error.criticalError
    }));
  } else {
    recordEvents(_defineProperty({}, EventTypes.loginServerError, {
      error: error.message,
      clientId: clientId,
      authType: authType,
      isCriticalError: error.criticalError
    }));
  }
}